/* eslint-disable */
import { MoveRight, PiggyBank, Plus } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../Components/ui/tabs2'
import { useEffect, useState } from 'react'
import { Button } from '../../Components/ui/button'
import { Refresh } from '@mui/icons-material'
import { MutualFundsOrders } from '../../store/MFStore'
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from 'recoil'
import { Card, CardContent, CardHeader, CardTitle } from '../..//Components/ui/card'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'
import { Link } from 'react-router-dom'
import { capitalizeWords, monthNames } from '../../utils/Util'
import MFOrdersSkeleton from '../../Components/SkeletonLoaders/MFOrdersSkeleton'
import { Skeleton } from '../../Components/ui/skeleton'
import ordersEmptyState from '../../assets/images/empty-state-orders.svg'
import SIPsEmpty from '../../Components/EmptyState/SIPsEmpty'
import ApiErrorState from '../../Components/Errors/ApiErrorState'

const ORDERS_PAGE_SIZE = 10

const MFIndividualOrders = () => {
  const orders = useRecoilValueLoadable(MutualFundsOrders)
  const [expandedIndex, setExpandedIndex] = useState(null)
  const refreshOrder = useRecoilRefresher_UNSTABLE(MutualFundsOrders)

  const [completedOrders, setCompletedOrders] = useState([])
  const [failedOrders, setFailedOrders] = useState([])
  const [visibleCounts, setVisibleCounts] = useState({
    completed: ORDERS_PAGE_SIZE,
    inProgress: ORDERS_PAGE_SIZE,
    failed: ORDERS_PAGE_SIZE,
  })
  const [loadingMoreTab, setLoadingMoreTab] = useState(null)

  const getOrderTime = (dateString) => {
    if (!dateString) return 0

    const normalizedDate = String(dateString).trim().replace('T', ' ')
    const [datePart, timePart = ''] = normalizedDate.split(/\s+/)
    const dateParts = datePart.split(datePart.includes('/') ? '/' : '-').map(Number)

    if (dateParts.length === 3 && dateParts.every((part) => !Number.isNaN(part))) {
      const [first, second, third] = dateParts
      const [year, month, day] =
        String(first).length === 4 ? [first, second, third] : [third, second, first]
      const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number)

      return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0).getTime()
    }

    const parsedDate = Date.parse(dateString)
    return Number.isNaN(parsedDate) ? 0 : parsedDate
  }

  const sortOrdersByDateDesc = (orders = []) =>
    [...orders].sort((a, b) => getOrderTime(b?.orderDate) - getOrderTime(a?.orderDate))

  const getSortedOrderGroups = (groupedOrders = {}) =>
    Object.entries(groupedOrders).sort(
      ([dateA, ordersA], [dateB, ordersB]) =>
        getOrderTime(ordersB?.[0]?.orderDate || dateB) -
        getOrderTime(ordersA?.[0]?.orderDate || dateA),
    )

  const getGroupedOrdersCount = (groupedOrders = {}) =>
    Object.values(groupedOrders).reduce((sum, groupOrders) => sum + (groupOrders?.length || 0), 0)

  const getLimitedGroupedOrders = (groupedOrders = {}, limit) => {
    const limited = {}
    let remaining = limit

    for (const [date, groupOrders] of getSortedOrderGroups(groupedOrders)) {
      if (remaining <= 0) break

      const slicedOrders = sortOrdersByDateDesc(groupOrders).slice(0, remaining)
      if (slicedOrders.length > 0) {
        limited[date] = slicedOrders
        remaining -= slicedOrders.length
      }
    }

    return limited
  }

  const handleLoadMore = (tab, totalCount) => {
    if (loadingMoreTab || visibleCounts[tab] >= totalCount) return

    setLoadingMoreTab(tab)
    window.setTimeout(() => {
      setVisibleCounts((prev) => ({
        ...prev,
        [tab]: Math.min(prev[tab] + ORDERS_PAGE_SIZE, totalCount),
      }))
      setLoadingMoreTab(null)
    }, 350)
  }

  const renderLoadMore = (tab, totalCount) => {
    if (visibleCounts[tab] >= totalCount) return null

    const isLoadingMore = loadingMoreTab === tab

    return (
      <div className='px-4 py-6 lg:px-0'>
        {isLoadingMore && (
          <div className='mb-4 space-y-3' aria-hidden='true'>
            {[1, 2].map((item) => (
              <div key={item} className='flex items-center justify-between p-4'>
                <div className='flex items-center gap-4'>
                  <Skeleton className='h-12 w-12 rounded-[8px]' />
                  <div className='space-y-2'>
                    <Skeleton className='h-4 w-48' />
                    <Skeleton className='h-4 w-20' />
                  </div>
                </div>
                <Skeleton className='h-9 w-20 rounded-md' />
              </div>
            ))}
          </div>
        )}
        <div className='flex justify-center'>
          <Button
            variant='outline'
            className='min-w-[140px]'
            loading={isLoadingMore}
            onClick={() => handleLoadMore(tab, totalCount)}
          >
            Load more
          </Button>
        </div>
      </div>
    )
  }

  const groupByDate = (orders) => {
    const ordersData = orders.reduce((acc, order) => {
      const date = order.orderDate.split(' ')[0] // Extract date portion
      if (!acc[date]) acc[date] = []
      acc[date].push(order)
      return acc
    }, {})

    // const newData = Object.entries(completedOrders).map(([date, orders]) =>
    //   transformToGroupedOrders(orders),
    // )
    // console.log('🚀 ~ groupByDate ~ newData:', newData)

    // setCompletedOrders(newData)

    return ordersData
  }

  useEffect(() => {
    if (orders.state === 'hasValue' && orders.contents?.orders?.length > 0) {
      const completed = orders?.contents?.orders?.filter(
        (order) =>
          order.currentStatus.toLowerCase() === 'allotment done' ||
          order.currentStatus.toLowerCase() === 'redemption done' ||
          order.currentStatus.toLowerCase() === 'order processed by exchange',
      )

      const failed = orders?.contents?.orders?.filter(
        (order) => order.currentStatus.toLowerCase() === 'failed',
      )

      setCompletedOrders(groupByDate(completed))
      setFailedOrders(groupByDate(failed))
      setVisibleCounts({
        completed: ORDERS_PAGE_SIZE,
        inProgress: ORDERS_PAGE_SIZE,
        failed: ORDERS_PAGE_SIZE,
      })
    } else if (orders.state === 'hasError') {
      console.error('Error fetching orders:', orders.contents)
    }
  }, [orders])

  useEffect(() => {
    refreshOrder()
  }, [])

  // Use regular expressions to extract the required information
  const extractInfo = (str) => {
    const regex = /SCHEME:\s*([^\s]+)\s*.*ORDER NO:\s*(\d+)/

    const match = str.match(regex)

    if (match) {
      return `Scheme: ${match[1]}, Order No: ${match[2]}`
    } else {
      return 'No match found'
    }
  }

  const transformToGroupedOrders = (orders) => {
    const groupedOrders = orders.reduce((acc, order) => {
      // Check if the order ID already exists in the accumulator
      if (!acc[order.schemeCode]) {
        acc[order.schemeCode] = { ...order } // Create a new object to store summed values
      } else {
        acc[order.schemeCode].allotedUnits =
          Number(acc[order.schemeCode].allotedUnits) + Number(order.allotedUnits) // Add units to existing sum
        acc[order.schemeCode].allotedAmt =
          Number(acc[order.schemeCode].allotedAmt) + Number(order.allotedAmt) // Add amount to existing sum
      }
      return acc
    }, {})

    const result = Object.values(groupedOrders)

    return result
  }

  const getDateMonthYear = (dateString) => {
    if (!dateString) return null // Handle null/undefined cases

    // Determine the correct delimiter
    const delimiter = dateString.includes('/') ? '/' : '-'

    return {
      date: dateString.split(delimiter)[0],
      month: dateString.split(delimiter)[1],
      year: dateString.split(delimiter)[2],
    }
  }
  console.log(orders)
  switch (orders.state) {
    case 'loading':
      return <MFOrdersSkeleton />
    case 'hasValue': {
      const inProgressOrders = sortOrdersByDateDesc(
        orders?.contents?.orders?.filter(
          (order) =>
            order.currentStatus.toLowerCase() === 'payment processed successfully' ||
            order.currentStatus.toLowerCase() === 'order placed successfully',
        ) || [],
      )
      const totalCompletedOrders = getGroupedOrdersCount(completedOrders)
      const totalFailedOrders = getGroupedOrdersCount(failedOrders)
      const totalInProgressOrders = inProgressOrders.length
      const visibleCompletedOrders = getLimitedGroupedOrders(
        completedOrders,
        visibleCounts.completed,
      )
      const visibleFailedOrders = getLimitedGroupedOrders(failedOrders, visibleCounts.failed)
      const visibleInProgressOrders = inProgressOrders.slice(0, visibleCounts.inProgress)

      return (
        <div className='py-0'>
          {orders?.contents?.status === 'SUCCESS' && orders?.contents?.orders.length > 0 && (
            <Tabs defaultValue='completed' className='max-w-[1128px] mx-auto mt-6'>
              <div className='flex items-center justify-center px-4 lg:px-0'>
                <TabsList className='grid h-11 w-full max-w-[420px] grid-cols-3 rounded-full md:w-auto'>
                  <TabsTrigger
                    value='completed'
                    className='h-9 w-full min-w-0 px-0.5 text-[9px] leading-3 sm:px-1.5 sm:text-xs md:px-4 md:text-sm'
                  >
                    Completed ({totalCompletedOrders})
                  </TabsTrigger>
                  <TabsTrigger
                    value='in-progress'
                    className='h-9 w-full min-w-0 px-0.5 text-[9px] leading-3 sm:px-1.5 sm:text-xs md:px-4 md:text-sm'
                  >
                    {`In-Progress (${totalInProgressOrders})`}
                  </TabsTrigger>
                  <TabsTrigger
                    value='failed'
                    className='h-9 w-full min-w-0 px-0.5 text-[9px] leading-3 sm:px-1.5 sm:text-xs md:px-4 md:text-sm'
                  >
                    Failed ({totalFailedOrders})
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value='in-progress' className='h-full pb-28 pt-3 lg:px-4 lg:pb-2'>
                {/* MF Orders list */}

                <div className='w-full mx-auto overflow-hidden fabits-compact-card-stack'>
                  {totalInProgressOrders > 0 ? (
                    <>
                      {visibleInProgressOrders.map((transaction, index) => (
                        <>
                          <div
                            key={transaction.orderNumber}
                            className='border-b border-gray-200 last:border-b-0 cursor-pointer px-4 lg:px-0'
                          >
                            {/* <div className='py-2 text-sm font-medium text-black-400 mb-4 mt-12 leading-5'>
                            {index}
                          </div> */}

                            {/* {orders.map((transaction, index) => ( */}
                            <div className={`p-4 flex items-center lg:items-start`}>
                              {/* <div
                              className={`flex-shrink-0 mr-6 text-[10px] flex flex-col justify-center items-center border p-2.5 rounded-[60px] relative leading-5 max-w-[58px] ${
                                transaction.buySell === 'PURCHASE'
                                  ? 'text-black-400'
                                  : 'text-black-400'
                              }`}
                            >
                              {index === 1 && (
                                <Error
                                  className='absolute -bottom-1 -right-1 h-4 w-4 text-error-500 z-2'
                                  sx={{ fontSize: '20px' }}
                                />
                              )}

                              {transaction.buySell === 'PURCHASE' ? (
                                <SouthEast sx={{ fontSize: '20px' }} />
                              ) : (
                                <NorthWest sx={{ fontSize: '20px' }} />
                              )}
                              {transaction.buySell === 'PURCHASE' ? 'Buy' : 'Sell'}
                            </div> */}
                              {/* <div className='hidden md:block mr-4'>
                                <img
                                  src={`${env.MF_LOGOS_URL}/${400034}.png`}
                                  alt=''
                                  className='h-12 w-12'
                                />
                              </div> */}

                              <div className='flex justify-between w-full'>
                                <div
                                  className='flex justify-between items-center w-full'
                                  onClick={() => {
                                    setExpandedIndex((prevIndex) =>
                                      prevIndex === index ? null : index,
                                    )
                                  }}
                                >
                                  <div>
                                    <div className='flex justify-between items-center gap-x-2'>
                                      <img
                                        src={`${env.MF_LOGOS_URL}/${transaction?.amcCode}.png`}
                                        className='h-12 w-12 rounded-[8px]'
                                      />
                                      <div>
                                        <p className='font-semibold text-gray-900  md:max-w-full text-wrap leading-6'>
                                          {transaction?.legalName
                                            ? transaction?.legalName
                                            : transaction?.schemeName}
                                        </p>

                                        {/* <p>{transaction?.orderRemarks}</p> */}
                                        <p
                                          className={`text-sm font-medium ${transaction?.currentStatus.toLowerCase() === 'failed'
                                            ? 'text-error-600'
                                            : transaction?.currentStatus.toLowerCase() ===
                                              'allotment done'
                                              ? 'text-secondary-600'
                                              : 'text-warning-600'
                                            }`}
                                        >
                                          {capitalizeWords(transaction?.currentStatus)}
                                        </p>

                                        <p className='text-sm font-medium text-black-400'>
                                          {transaction?.orderDate ? transaction?.orderDate : null}
                                        </p>
                                      </div>

                                      {/* <span className='f0lex-shrink-0 text-black-200 lg:hidden'>
                                        <KeyboardArrowDown sx={{ fontSize: '24px' }} />
                                      </span> */}
                                    </div>

                                    {/* <p
                                  className={`text-sm font-medium text-black-400 leading-5 ${
                                    transaction.status.includes('failed')
                                      ? 'text-red-500'
                                      : 'text-gray-500'
                                  }`}
                                  >
                                    {transaction.buySell}
                                  </p> */}

                                    {/* <div className='flex justify-between items-center text-right mt-2 lg:hidden'>
                                    <p className='text-sm font-medium text-black-400'>
                                      {transaction.qty ? transaction.qty : '#'}
                                      Units
                                    </p>
                                    <p className='font-semibold'>
                                      ₹ {transaction.amount.toLocaleString()}
                                    </p>
                                  </div> */}

                                    {/* <div className='hidden lg:flex justify-end items-center gap-x-1 min-w-[150px]'>
                      <ChevronDown className='flex-shrink-0 w-5 h-5 text-black-200' />
                    </div> */}
                                  </div>

                                  {/* <div className='hidden lg:flex leading-5 flex-shrink-0 text-right w-[100px]'>
                                    {transaction.allotedUnits && (
                                      <p className='text-sm font-medium text-black-400'>
                                        {transaction.allotedUnits} Units
                                      </p>
                                    )}
                                  </div> */}

                                  {/* <div className='hidden lg:flex justify-end items-center gap-x-1 min-w-[150px]'>
                                    <p className='font-semibold leading-6'>
                                      ₹ {transaction.allotedAmt ? +transaction.allotedAmt : ''}
                                    </p>
                                    <KeyboardArrowDown
                                      className='flex-shrink-0 text-black-200'
                                      sx={{ fontSize: '24px' }}
                                    />
                                  </div> */}
                                </div>
                                {/* <div
                                className={`text-sm mr-10 hidden lg:flex justify-between items-center border-t border-dashed text-gray-500 overflow-hidden transition-all duration-300 ease-in-out ${
                                  expandedIndex === index
                                    ? 'max-h-48 opacity-100 mt-4 pt-4'
                                    : 'max-h-0 opacity-0'
                                }`}
                              >
                                <div className='flex items-center justify-between gap-x-2 leading-5'>
                                  <span className='font-medium text-sm text-black-400'>
                                    Order ID
                                  </span>
                                  <div className='flex items-center space-x-2'>
                                    <span className='font-semibold text-black-500 text-sm'>
                                      FAB-01559872
                                    </span>
                                    <Button size='simple' variant='ghost'>
                                      <ContentCopy
                                        className='text-primary-500'
                                        sx={{ fontSize: '16px' }}
                                      />
                                      <span className='sr-only'>Copy order ID</span>
                                    </Button>
                                  </div>
                                </div>

                                <div className='flex items-center justify-between gap-x-2 leading-5'>
                                  <span className='font-medium text-sm text-black-400'>
                                    SIP Reference No.
                                  </span>
                                  <div className='flex items-center space-x-2'>
                                    <span className='font-semibold text-black-500 text-sm'>
                                      123456788765432
                                    </span>
                                    <Button size='simple' variant='ghost'>
                                      <ContentCopy
                                        className='text-primary-500'
                                        sx={{ fontSize: '16px' }}
                                      />
                                      <span className='sr-only'>Copy SIP reference number</span>
                                    </Button>
                                  </div>
                                </div>
                                <div className='flex items-center justify-between gap-x-2 leading-5'>
                                  <span className='font-medium text-sm text-black-400'>
                                    SIP Registration No.
                                  </span>
                                  <div className='flex items-center space-x-2'>
                                    <span className='font-semibold text-black-500 text-sm'>
                                      FAB-01559872
                                    </span>
                                    <Button size='simple' variant='ghost'>
                                      <ContentCopy
                                        className='text-primary-500'
                                        sx={{ fontSize: '16px' }}
                                      />
                                      <span className='sr-only'>Copy SIP registration number</span>
                                    </Button>
                                  </div>
                                </div>
                              </div> */}
                              </div>
                            </div>
                            {/* ))} */}
                            {/* <div
                            className={`mt-4 space-y-2 text-xs flex flex-col gap-y-4 lg:hidden border-t border-dashed text-gray-500 overflow-hidden transition-all duration-300 ease-in-out ${
                              expandedIndex === index
                                ? 'max-h-48 opacity-100 py-3'
                                : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>Order ID</span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500 text-xs'>
                                  FAB-01559872
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy order ID</span>
                                </Button>
                              </div>
                            </div>

                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>
                                SIP Reference No.
                              </span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500  text-xs'>
                                  123456788765432
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy SIP reference number</span>
                                </Button>
                              </div>
                            </div>
                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>
                                SIP Registration No.
                              </span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500  text-xs'>
                                  FAB-01559872
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy SIP registration number</span>
                                </Button>
                              </div>
                            </div>
                          </div> */}
                          </div>
                        </>
                      ))}
                      {renderLoadMore('inProgress', totalInProgressOrders)}
                    </>
                  ) : (
                    <SIPsEmpty
                      title='You have no orders in progress at the moment.'
                      description='It seems like you haven’t placed any new order yet. Once you place an order, it will appear here.'
                      mainText=''
                      page='/dashboard/mutual-funds'
                      showButton={false}
                      variant='inProgress'
                    />
                  )}
                </div>
              </TabsContent>

              <TabsContent value='completed' className='h-full pb-28 pt-3 lg:px-4 lg:pb-2'>
                {/* MF Orders list */}

                <div className='w-full mx-auto overflow-hidden fabits-compact-card-stack'>
                  {totalCompletedOrders > 0 ? (
                    <>
                      {getSortedOrderGroups(visibleCompletedOrders).map(([date, orders]) => (
                        <>
                          <div
                            key={orders[0].orderDate}
                            className='border-b border-gray-200 last:border-b-0 cursor-pointer px-4 lg:px-0'
                          >
                            <div className='fabits-compact-card-heading py-2 text-sm font-medium text-black-400 leading-5'>
                              {getDateMonthYear(orders[0].orderDate).date}{' '}
                              {monthNames[getDateMonthYear(orders[0].orderDate).month - 1]}{' '}
                              {getDateMonthYear(orders[0].orderDate).year}
                            </div>

                            {sortOrdersByDateDesc(orders).map((transaction, index) => (
                              <div className={`p-4 flex items-center lg:items-start`}>
                                {/* <div
                              className={`flex-shrink-0 mr-6 text-[10px] flex flex-col justify-center items-center border p-2.5 rounded-[60px] relative leading-5 max-w-[58px] ${
                                transaction.buySell === 'PURCHASE'
                                  ? 'text-black-400'
                                  : 'text-black-400'
                              }`}
                            >
                              {index === 1 && (
                                <Error
                                  className='absolute -bottom-1 -right-1 h-4 w-4 text-error-500 z-2'
                                  sx={{ fontSize: '20px' }}
                                />
                              )}

                              {transaction.buySell === 'PURCHASE' ? (
                                <SouthEast sx={{ fontSize: '20px' }} />
                              ) : (
                                <NorthWest sx={{ fontSize: '20px' }} />
                              )}
                              {transaction.buySell === 'PURCHASE' ? 'Buy' : 'Sell'}
                            </div> */}
                                {/* <div className='hidden md:block mr-4'>
                                <img
                                  src={`${env.MF_LOGOS_URL}/${400034}.png`}
                                  alt=''
                                  className='h-12 w-12'
                                />
                              </div> */}

                                <div className='flex justify-between w-full'>
                                  <div
                                    className='flex justify-between items-center w-full'
                                    onClick={() => {
                                      setExpandedIndex((prevIndex) =>
                                        prevIndex === index ? null : index,
                                      )
                                    }}
                                  >
                                    <div className='flex items-center gap-x-2 w-full'>
                                      <img
                                        src={`${env.MF_LOGOS_URL}/${transaction?.amcCode}.png`}
                                        className='h-12 w-12 rounded-[8px]'
                                      />
                                      <div className='w-full'>
                                        <div>
                                          <p className='font-semibold text-gray-900  md:max-w-full text-wrap leading-6'>
                                            {transaction?.legalName
                                              ? transaction?.legalName.replace(/Fund.*$/, 'Fund')
                                              : transaction?.schemeName.replace(
                                                /Fund.*$/,
                                                'Fund',
                                              )}{' '}
                                          </p>
                                          {/* <p>{transaction?.orderRemarks}</p> */}
                                          <p
                                            className={`text-sm flex gap-x-2 font-medium ${transaction?.currentStatus.toLowerCase() === 'failed'
                                              ? 'text-error-600'
                                              : transaction?.currentStatus.toLowerCase() ===
                                                'allotment done'
                                                ? 'text-secondary-600'
                                                : 'text-warning-600'
                                              }`}
                                          >
                                            {capitalizeWords(transaction?.currentStatus)}
                                          </p>
                                        </div>
                                        <div className='flex lg:hidden justify-between items-center'>
                                          <div className='flex leading-5 flex-shrink-0 text-right'>
                                            {transaction.allotedUnits && (
                                              <p className='text-sm font-medium text-black-400'>
                                                {toCurrency(transaction?.allotedUnits)} Units
                                              </p>
                                            )}
                                          </div>

                                          <div className='flex justify-end items-center gap-x-1'>
                                            {transaction.allotedAmt && (
                                              <p className='font-semibold leading-6'>
                                                ₹{' '}
                                                {transaction.allotedAmt
                                                  ? toCurrency(+transaction.allotedAmt)
                                                  : ''}
                                              </p>
                                            )}
                                            {/* <KeyboardArrowDown
                                      className='flex-shrink-0 text-black-200'
                                      sx={{ fontSize: '24px' }}
                                    /> */}
                                          </div>
                                        </div>
                                      </div>

                                      {/* <span className='f0lex-shrink-0 text-black-200 lg:hidden'>
                                        <KeyboardArrowDown sx={{ fontSize: '24px' }} />
                                      </span> */}
                                    </div>

                                    {/* <p
                                  className={`text-sm font-medium text-black-400 leading-5 ${
                                    transaction.status.includes('failed')
                                      ? 'text-red-500'
                                      : 'text-gray-500'
                                  }`}
                                  >
                                    {transaction.buySell}
                                  </p> */}

                                    {/* <div className='flex justify-between items-center text-right mt-2 lg:hidden'>
                                    <p className='text-sm font-medium text-black-400'>
                                      {transaction.qty ? transaction.qty : '#'}
                                      Units
                                    </p>
                                    <p className='font-semibold'>
                                      ₹ {transaction.amount.toLocaleString()}
                                    </p>
                                  </div> */}

                                    {/* <div className='hidden lg:flex justify-end items-center gap-x-1 min-w-[150px]'>
                      <ChevronDown className='flex-shrink-0 w-5 h-5 text-black-200' />
                    </div> */}

                                    <div className='hidden md:flex justify-center items-center'>
                                      <div className='hidden lg:flex leading-5 flex-shrink-0 text-right w-[100px]'>
                                        {transaction.allotedUnits && (
                                          <p className='text-sm font-medium text-black-400'>
                                            {toCurrency(transaction?.allotedUnits)} Units
                                          </p>
                                        )}
                                      </div>

                                      <div className='hidden lg:flex justify-end items-center gap-x-1 min-w-[150px]'>
                                        {transaction.allotedAmt && (
                                          <p className='font-semibold leading-6'>
                                            ₹{' '}
                                            {transaction.allotedAmt
                                              ? toCurrency(+transaction.allotedAmt)
                                              : ''}
                                          </p>
                                        )}
                                        {/* <KeyboardArrowDown
                                      className='flex-shrink-0 text-black-200'
                                      sx={{ fontSize: '24px' }}
                                    /> */}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {/* <div
                              className={`text-sm mr-10 hidden lg:flex justify-between items-center border-t border-dashed text-gray-500 overflow-hidden transition-all duration-300 ease-in-out ${
                                expandedIndex === index
                                  ? 'max-h-48 opacity-100 mt-4 pt-4'
                                  : 'max-h-0 opacity-0'
                              }`}
                            >
                              <div className='flex items-center justify-between gap-x-2 leading-5'>
                                <span className='font-medium text-sm text-black-400'>Order ID</span>
                                <div className='flex items-center space-x-2'>
                                  <span className='font-semibold text-black-500 text-sm'>
                                    FAB-01559872
                                  </span>
                                  <Button size='simple' variant='ghost'>
                                    <ContentCopy
                                      className='text-primary-500'
                                      sx={{ fontSize: '16px' }}
                                    />
                                    <span className='sr-only'>Copy order ID</span>
                                  </Button>
                                </div>
                              </div>

                              <div className='flex items-center justify-between gap-x-2 leading-5'>
                                <span className='font-medium text-sm text-black-400'>
                                  SIP Reference No.
                                </span>
                                <div className='flex items-center space-x-2'>
                                  <span className='font-semibold text-black-500 text-sm'>
                                    123456788765432
                                  </span>
                                  <Button size='simple' variant='ghost'>
                                    <ContentCopy
                                      className='text-primary-500'
                                      sx={{ fontSize: '16px' }}
                                    />
                                    <span className='sr-only'>Copy SIP reference number</span>
                                  </Button>
                                </div>
                              </div>
                              <div className='flex items-center justify-between gap-x-2 leading-5'>
                                <span className='font-medium text-sm text-black-400'>
                                  SIP Registration No.
                                </span>
                                <div className='flex items-center space-x-2'>
                                  <span className='font-semibold text-black-500 text-sm'>
                                    FAB-01559872
                                  </span>
                                  <Button size='simple' variant='ghost'>
                                    <ContentCopy
                                      className='text-primary-500'
                                      sx={{ fontSize: '16px' }}
                                    />
                                    <span className='sr-only'>Copy SIP registration number</span>
                                  </Button>
                                </div>
                              </div>
                            </div> */}
                            {/* ))} */}
                            {/* <div
                            className={`mt-4 space-y-2 text-xs flex flex-col gap-y-4 lg:hidden border-t border-dashed text-gray-500 overflow-hidden transition-all duration-300 ease-in-out ${
                              expandedIndex === index
                                ? 'max-h-48 opacity-100 py-3'
                                : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>Order ID</span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500 text-xs'>
                                  FAB-01559872
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy order ID</span>
                                </Button>
                              </div>
                            </div>

                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>
                                SIP Reference No.
                              </span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500  text-xs'>
                                  123456788765432
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy SIP reference number</span>
                                </Button>
                              </div>
                            </div>
                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>
                                SIP Registration No.
                              </span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500  text-xs'>
                                  FAB-01559872
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy SIP registration number</span>
                                </Button>
                              </div>
                            </div>
                          </div> */}
                          </div>
                        </>
                      ))}
                      {renderLoadMore('completed', totalCompletedOrders)}
                    </>
                  ) : (
                    <SIPsEmpty
                      title='No completed orders found.'
                      description='There are no completed orders in your account. Completed orders will be listed here once processed successfully.'
                      mainText=''
                      page='/dashboard/mutual-funds'
                      showButton={false}
                      variant='completed'
                    />
                  )}
                </div>
              </TabsContent>

              <TabsContent value='failed' className='h-full pb-28 pt-3 lg:px-4 lg:pb-2'>
                {/* MF Orders list */}

                <div className='w-full mx-auto overflow-hidden fabits-compact-card-stack'>
                  {totalFailedOrders > 0 ? (
                    <>
                      {getSortedOrderGroups(visibleFailedOrders).map(([date, orders]) => (
                        <>
                          <div
                            key={date}
                            className='border-b border-gray-200 last:border-b-0 cursor-pointer px-4 lg:px-0'
                          >
                            <div className='fabits-compact-card-heading py-2 text-sm font-medium text-black-400 leading-5'>
                              {getDateMonthYear(orders[0].orderDate).date}{' '}
                              {monthNames[getDateMonthYear(orders[0].orderDate).month - 1]}{' '}
                              {getDateMonthYear(orders[0].orderDate).year}
                            </div>

                            {sortOrdersByDateDesc(orders).map((transaction, index) => (
                              <div className={`p-4 flex items-center lg:items-start`}>
                                {/* <div
                              className={`flex-shrink-0 mr-6 text-[10px] flex flex-col justify-center items-center border p-2.5 rounded-[60px] relative leading-5 max-w-[58px] ${
                                transaction.buySell === 'PURCHASE'
                                  ? 'text-black-400'
                                  : 'text-black-400'
                              }`}
                            >
                              {index === 1 && (
                                <Error
                                  className='absolute -bottom-1 -right-1 h-4 w-4 text-error-500 z-2'
                                  sx={{ fontSize: '20px' }}
                                />
                              )}

                              {transaction.buySell === 'PURCHASE' ? (
                                <SouthEast sx={{ fontSize: '20px' }} />
                              ) : (
                                <NorthWest sx={{ fontSize: '20px' }} />
                              )}
                              {transaction.buySell === 'PURCHASE' ? 'Buy' : 'Sell'}
                            </div> */}
                                {/* <div className='hidden md:block mr-4'>
                                <img
                                  src={`${env.MF_LOGOS_URL}/${400034}.png`}
                                  alt=''
                                  className='h-12 w-12'
                                />
                              </div> */}

                                <div className='flex justify-between w-full'>
                                  <div
                                    className='flex justify-between items-center w-full'
                                    onClick={() => {
                                      setExpandedIndex((prevIndex) =>
                                        prevIndex === index ? null : index,
                                      )
                                    }}
                                  >
                                    <div>
                                      <div className='flex justify-between items-center gap-x-2'>
                                        <img
                                          src={`${env.MF_LOGOS_URL}/${transaction?.amcCode}.png`}
                                          className='h-12 w-12 rounded-[8px]'
                                        />
                                        <div>
                                          <p className='font-semibold text-gray-900  md:max-w-full text-wrap leading-6'>
                                            {transaction?.legalName
                                              ? transaction?.legalName
                                              : transaction?.schemeName}
                                          </p>

                                          {/* <p>{transaction?.orderRemarks}</p> */}
                                          <p
                                            className={`text-sm font-medium ${transaction?.currentStatus.toLowerCase() === 'failed'
                                              ? 'text-error-600'
                                              : transaction?.currentStatus.toLowerCase() ===
                                                'allotment done'
                                                ? 'text-secondary-600'
                                                : 'text-warning-600'
                                              }`}
                                          >
                                            {capitalizeWords(transaction?.currentStatus)}
                                          </p>
                                        </div>

                                        {/* <span className='f0lex-shrink-0 text-black-200 lg:hidden'>
                                        <KeyboardArrowDown sx={{ fontSize: '24px' }} />
                                      </span> */}
                                      </div>

                                      {/* <p
                                  className={`text-sm font-medium text-black-400 leading-5 ${
                                    transaction.status.includes('failed')
                                      ? 'text-red-500'
                                      : 'text-gray-500'
                                  }`}
                                  >
                                    {transaction.buySell}
                                  </p> */}

                                      {/* <div className='flex justify-between items-center text-right mt-2 lg:hidden'>
                                    <p className='text-sm font-medium text-black-400'>
                                      {transaction.qty ? transaction.qty : '#'}
                                      Units
                                    </p>
                                    <p className='font-semibold'>
                                      ₹ {transaction.amount.toLocaleString()}
                                    </p>
                                  </div> */}

                                      {/* <div className='hidden lg:flex justify-end items-center gap-x-1 min-w-[150px]'>
                      <ChevronDown className='flex-shrink-0 w-5 h-5 text-black-200' />
                    </div> */}
                                    </div>

                                    {/* <div className='hidden lg:flex leading-5 flex-shrink-0 text-right w-[100px]'>
                                    {transaction.allotedUnits && (
                                      <p className='text-sm font-medium text-black-400'>
                                        {transaction.allotedUnits} Units
                                      </p>
                                    )}
                                  </div> */}
                                    {/* 
                                  <div className='hidden lg:flex justify-end items-center gap-x-1 min-w-[150px]'>
                                    <p className='font-semibold leading-6'>
                                      ₹ {transaction.allotedAmt ? +transaction.allotedAmt : ''}
                                    </p>
                                    <KeyboardArrowDown
                                      className='flex-shrink-0 text-black-200'
                                      sx={{ fontSize: '24px' }}
                                    />
                                  </div> */}
                                  </div>
                                  {/* <div
                                className={`text-sm mr-10 hidden lg:flex justify-between items-center border-t border-dashed text-gray-500 overflow-hidden transition-all duration-300 ease-in-out ${
                                  expandedIndex === index
                                    ? 'max-h-48 opacity-100 mt-4 pt-4'
                                    : 'max-h-0 opacity-0'
                                }`}
                              >
                                <div className='flex items-center justify-between gap-x-2 leading-5'>
                                  <span className='font-medium text-sm text-black-400'>
                                    Order ID
                                  </span>
                                  <div className='flex items-center space-x-2'>
                                    <span className='font-semibold text-black-500 text-sm'>
                                      FAB-01559872
                                    </span>
                                    <Button size='simple' variant='ghost'>
                                      <ContentCopy
                                        className='text-primary-500'
                                        sx={{ fontSize: '16px' }}
                                      />
                                      <span className='sr-only'>Copy order ID</span>
                                    </Button>
                                  </div>
                                </div>

                                <div className='flex items-center justify-between gap-x-2 leading-5'>
                                  <span className='font-medium text-sm text-black-400'>
                                    SIP Reference No.
                                  </span>
                                  <div className='flex items-center space-x-2'>
                                    <span className='font-semibold text-black-500 text-sm'>
                                      123456788765432
                                    </span>
                                    <Button size='simple' variant='ghost'>
                                      <ContentCopy
                                        className='text-primary-500'
                                        sx={{ fontSize: '16px' }}
                                      />
                                      <span className='sr-only'>Copy SIP reference number</span>
                                    </Button>
                                  </div>
                                </div>
                                <div className='flex items-center justify-between gap-x-2 leading-5'>
                                  <span className='font-medium text-sm text-black-400'>
                                    SIP Registration No.
                                  </span>
                                  <div className='flex items-center space-x-2'>
                                    <span className='font-semibold text-black-500 text-sm'>
                                      FAB-01559872
                                    </span>
                                    <Button size='simple' variant='ghost'>
                                      <ContentCopy
                                        className='text-primary-500'
                                        sx={{ fontSize: '16px' }}
                                      />
                                      <span className='sr-only'>Copy SIP registration number</span>
                                    </Button>
                                  </div>
                                </div>
                              </div> */}
                                </div>
                              </div>
                            ))}
                            {/* <div
                            className={`mt-4 space-y-2 text-xs flex flex-col gap-y-4 lg:hidden border-t border-dashed text-gray-500 overflow-hidden transition-all duration-300 ease-in-out ${
                              expandedIndex === index
                                ? 'max-h-48 opacity-100 py-3'
                                : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>Order ID</span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500 text-xs'>
                                  FAB-01559872
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy order ID</span>
                                </Button>
                              </div>
                            </div>

                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>
                                SIP Reference No.
                              </span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500  text-xs'>
                                  123456788765432
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy SIP reference number</span>
                                </Button>
                              </div>
                            </div>
                            <div className='flex items-center justify-between gap-x-2'>
                              <span className='font-medium text-xs text-black-400'>
                                SIP Registration No.
                              </span>
                              <div className='flex items-center space-x-2'>
                                <span className='font-semibold text-black-500  text-xs'>
                                  FAB-01559872
                                </span>
                                <Button size='simple' variant='ghost'>
                                  <CopyIcon className='h-4 w-4 rotate-90 text-primary-500' />
                                  <span className='sr-only'>Copy SIP registration number</span>
                                </Button>
                              </div>
                            </div>
                          </div> */}
                          </div>
                        </>
                      ))}
                      {renderLoadMore('failed', totalFailedOrders)}
                    </>
                  ) : (
                    <SIPsEmpty
                      title='No failed orders to display.'
                      description='Good news! You don’t have any failed orders. If an issue occurs with an order, it will be shown here for your reference.'
                      mainText=''
                      page='/dashboard/mutual-funds'
                      showButton={false}
                      variant='failed'
                    />
                  )}
                </div>
              </TabsContent>
              {/* <TabsContent value='Basket'>b</TabsContent> */}
            </Tabs>
          )}

          {(orders?.contents?.status === 'SUCCESS' ||
            orders?.contents?.response?.status === 'FAILURE') &&
            (orders?.contents?.orders?.length === 0 ||
              orders?.contents?.response?.message ===
              'No order history found for the specified user.') && (
              <div className='bg-white flex items-center justify-center'>
                <Card className='w-full max-w-2xl text-center border-none shadow-none'>
                  <CardHeader className='flex flex-col justify-center items-center space-y-2'>
                    <img
                      className='mx-auto h-auto w-[240px] max-w-[68vw] sm:w-[260px]'
                      src={ordersEmptyState}
                      alt=''
                      aria-hidden='true'
                      draggable='false'
                    />
                    <CardTitle className='text-xl'>
                      You haven’t placed any mutual fund orders yet
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className='text-muted-foreground mb-6'>
                      Start building your investment portfolio by placing your first mutual fund
                      order.
                    </p>
                    <Link to='/dashboard/mutual-funds'>
                      <Button className='w-full'>
                        <Plus className='mr-2 h-4 w-4' /> Invest Now
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            )}

          {orders?.contents?.status === 'FAILURE' && (
            <div className='bg-white flex items-center justify-center'>
              <ApiErrorState>
                <div className='fabits-api-error-actions'>
                  <Button
                    className='w-full'
                    variant='fabitsWhite'
                    onClick={() => window.location.reload()}
                  >
                    <Refresh className='mr-2 h-4 w-4 text-black-300' /> Refresh
                  </Button>

                  <Link className='flex-1' to='/dashboard/mutual-funds'>
                    <Button className='w-full'>
                      View Funds
                      <MoveRight className='ml-2 h-4 w-4' />
                    </Button>
                  </Link>
                </div>
              </ApiErrorState>
            </div>
          )}
        </div>
      )
    }

    case 'hasError':
      return (
        <div className='bg-white flex items-center justify-center'>
          <ApiErrorState />
        </div>
      )

    default:
      return null
  }
}

export const Boilerplate = ({ state, desc }) => {
  return (
    <div className='h-3/5 flex items-center justify-center p-4'>
      <Card className='w-full max-w-sm text-center border-none shadow-none'>
        <CardHeader>
          <div className='w-20 h-20 mx-auto mb-4 flex justify-center items-center'>
            <img src={`${env.SVGS}/error-icon.svg`} />
          </div>
          <CardTitle className='text-xl'>{state}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-muted-foreground mb-6'>{desc}</p>
          {/* <Link to='/dashboard/mutual-funds'>
            <Button className='w-full'>
              <Plus className='mr-2 h-4 w-4' /> Create SIP
            </Button>
          </Link> */}
        </CardContent>
      </Card>
    </div>
  )
}

export default MFIndividualOrders
